import { ReactNode } from 'react';
import { NavLink, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';

interface AdminLayoutProps {
  children: ReactNode;
  title: string;
}

const AdminLayout = ({ children, title }: AdminLayoutProps) => {
  const { auth, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header con efecto de paralaje similar al AccountPage */}
      <div className="relative h-48 bg-black">
        <div className="absolute inset-0 bg-gradient-to-r from-gray-900 to-black opacity-90"></div>
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute inset-0 bg-[url('/bg-pattern.svg')] bg-center opacity-10"></div>
        </div>
        <div className="absolute inset-0 flex items-center">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-white">{title}</h1>
                <p className="text-gray-300 mt-1">Panel de administración</p>
              </div>
              <div className="flex items-center gap-4">
                <div className="text-right">
                  <p className="text-white text-sm font-medium">{auth.user?.name}</p>
                  <p className="text-gray-400 text-xs">{auth.user?.role}</p>
                </div>
                <div className="h-12 w-12 rounded-full bg-gray-700 border-2 border-gray-600 flex items-center justify-center text-white text-xl">
                  {auth.user?.name?.charAt(0).toUpperCase() || 'U'}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>


      {/* Navegación superior */}
      <div className="sticky top-0 bg-white shadow-sm z-10 w-full overflow-hidden">
        <style>{`
          .admin-nav-scrollbar::-webkit-scrollbar {
            height: 4px;
          }
          .admin-nav-scrollbar::-webkit-scrollbar-track {
            background: transparent;
            border-radius: 4px;
          }
          .admin-nav-scrollbar::-webkit-scrollbar-thumb {
            background: #e5e7eb;
            border-radius: 8px;
          }
          .admin-nav-scrollbar::-webkit-scrollbar-thumb:hover {
            background: #d1d5db;
          }
          .admin-nav-scrollbar {
            scrollbar-width: thin;
            scrollbar-color: #e5e7eb transparent;
          }
        `}</style>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex space-x-1 sm:space-x-4 overflow-x-auto admin-nav-scrollbar items-center flex-1 mr-4 whitespace-nowrap pb-1 pt-1">
              <NavLink
                to="/admin/dashboard"
                className={({ isActive }) =>
                  `px-3 py-2 text-sm font-medium ${isActive ? 'text-black border-b-2 border-black' : 'text-gray-600 hover:text-black'}`
                }
              >
                Dashboard
              </NavLink>
              <NavLink
                to="/admin/users"
                className={({ isActive }) =>
                  `px-3 py-2 text-sm font-medium ${isActive ? 'text-black border-b-2 border-black' : 'text-gray-600 hover:text-black'}`
                }
              >
                Usuarios
              </NavLink>
              <NavLink
                to="/admin/users/pending"
                className={({ isActive }) =>
                  `px-3 py-2 text-sm font-medium ${isActive ? 'text-orange-600 border-b-2 border-orange-600' : 'text-orange-500 hover:text-orange-600'}`
                }
              >
                Pendientes
              </NavLink>
              <NavLink
                to="/admin/products"
                className={({ isActive }) =>
                  `px-3 py-2 text-sm font-medium ${isActive ? 'text-black border-b-2 border-black' : 'text-gray-600 hover:text-black'}`
                }
              >
                Productos
              </NavLink>
              <NavLink
                to="/admin/orders"
                className={({ isActive }) =>
                  `px-3 py-2 text-sm font-medium ${isActive ? 'text-black border-b-2 border-black' : 'text-gray-600 hover:text-black'}`
                }
              >
                Pedidos
              </NavLink>
              <NavLink
                to="/admin/categories"
                className={({ isActive }) =>
                  `px-3 py-2 text-sm font-medium ${isActive ? 'text-black border-b-2 border-black' : 'text-gray-600 hover:text-black'}`
                }
              >
                Categorías
              </NavLink>
              <NavLink
                to="/admin/analytics"
                className={({ isActive }) =>
                  `px-3 py-2 text-sm font-medium ${isActive ? 'text-black border-b-2 border-black' : 'text-gray-600 hover:text-black'}`
                }
              >
                Analíticas
              </NavLink>
              <NavLink
                to="/admin/reviews"
                className={({ isActive }) =>
                  `px-3 py-2 text-sm font-medium ${isActive ? 'text-black border-b-2 border-black' : 'text-gray-600 hover:text-black'}`
                }
              >
                Reseñas
              </NavLink>
              <NavLink
                to="/admin/settings"
                className={({ isActive }) =>
                  `px-3 py-2 text-sm font-medium ${isActive ? 'text-black border-b-2 border-black' : 'text-gray-600 hover:text-black'}`
                }
              >
                Configuración
              </NavLink>
              <NavLink
                to="/admin/discounts"
                className={({ isActive }) =>
                  `px-3 py-2 text-sm font-medium ${isActive ? 'text-green-600 border-b-2 border-green-600' : 'text-green-500 hover:text-green-600'}`
                }
              >
                Descuentos
              </NavLink>
              <NavLink
                to="/admin/popups"
                className={({ isActive }) =>
                  `px-3 py-2 text-sm font-medium ${isActive ? 'text-purple-600 border-b-2 border-purple-600' : 'text-purple-500 hover:text-purple-600'}`
                }
              >
                Popups
              </NavLink>
              <NavLink
                to="/admin/store-categories"
                className={({ isActive }) =>
                  `px-3 py-2 text-sm font-medium ${isActive ? 'text-blue-600 border-b-2 border-blue-600' : 'text-blue-500 hover:text-blue-600'}`
                }
              >
                Mapa
              </NavLink>
            </div>

            <div className="flex items-center flex-shrink-0 pl-2 border-l border-gray-100">
              <Link to="/" className="text-sm text-gray-600 hover:text-black mr-4 hidden sm:block ml-4">
                Volver a la tienda
              </Link>
              <button
                onClick={handleLogout}
                className="text-sm bg-black text-white px-4 py-1 rounded-full hover:bg-gray-800"
              >
                Cerrar sesión
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Contenido principal */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {children}
      </main>
    </div >
  );
};

export default AdminLayout; 